import { useQueries } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate, NavLink, useParams, useSearchParams } from 'react-router-dom';

import { EmptyState, ErrorState } from '../../components/ui/Feedback';
import { IconPencil, IconShield } from '../../components/layout/icons';
import { SearchableSelect } from '../../components/ui/SearchableSelect';
import { SortableTh } from '../../components/ui/SortableTh';
import { TableSkeleton } from '../../components/ui/Skeleton';
import { ApiError } from '../../lib/api-client';
import { confirmAction, confirmUpdate } from '../../lib/confirm-service';
import { parseNoticeContent } from '../../lib/notice-content';
import { stripHtml } from '../../lib/rich-text';
import { useTableSort } from '../../lib/table-sort';
import { GeneralSettingsPanel } from './GeneralSettingsPanel';
import { SettingsFormDrawer } from './SettingsFormDrawer';
import {
  ChargeSettingsDrawer,
  DocumentHeaderSettingsDrawer,
  PackageSettingsDrawer,
} from './SpecializedSettingsDrawers';
import { TdsManagementPanel } from './TdsManagementPanel';
import { fetchSettingsLookup } from './settings.api';
import {
  isSettingsModuleSlug,
  settingsModuleMap,
  settingsModules,
  settingsModuleTabs,
  settingsNavigationLinks,
  usersSettingsTabs,
} from './settings.modules';
import {
  settingsKeys,
  useCreateSetting,
  useSettingsLookup,
  useSettingsList,
  useToggleSettingStatus,
  useUpdateSetting,
} from './settings.queries';
import { clampSettingsPage, formatSettingsValue, getValueAtPath, lookupRecordLabel, settingsRecordLabel } from './settings.utils';
import type {
  SettingsModuleDefinition,
  SettingsModuleSlug,
  SettingsRecord,
  SettingsStatus,
} from './types';

const errorMessage = (error: unknown, fallback: string) => error instanceof ApiError ? error.message : fallback;

const statusFilterOptions = [
  { value: '', label: 'Active & inactive' },
  { value: 'active', label: 'Active only' },
  { value: 'inactive', label: 'Inactive only' },
];

const rowsPerPageOptions = [
  { value: '10', label: '10' },
  { value: '20', label: '20' },
  { value: '50', label: '50' },
];

/** Sortable column paths per module, mirroring the backend searchFields whitelist plus createdAt. */
const SETTINGS_SORTABLE_FIELDS: Partial<Record<SettingsModuleSlug, readonly string[]>> = {
  'charge-categories': ['name', 'description'],
  'charge-sections': ['name'],
  'requisition-sections': ['name'],
  charges: ['name', 'template', 'note', 'parameters.test', 'parameters.lisCode'],
  'test-samples': ['sampleName'],
  'tpa-management': ['tpaName', 'contactPersonName', 'contactPersonPhone'],
  vials: ['vialName'],
  wards: ['wardName'],
  beds: ['bedName'],
  diagnoses: ['diagnosisName', 'icdCode'],
  packages: ['packageName'],
  'document-headers': ['headerName'],
  users: ['employeeId', 'name', 'email', 'phone', 'role'],
  roles: ['name'],
  permissions: ['name'],
  doctors: ['registrationId', 'name', 'phone', 'specialization'],
  departments: ['departmentName', 'departmentCode'],
  notices: ['notice'],
  referral: ['referralName', 'phoneNo', 'address'],
  'dialysis-machines': ['machineName'],
  'ot-rooms': ['roomName'],
  'procedure-types': ['procedureName'],
};

const SettingsLoading = () => <TableSkeleton rows={6} columns={5} label="Loading settings" />;

const pageNumbers = (current: number, total: number): Array<number | 'ellipsis-left' | 'ellipsis-right'> => {
  if (total <= 7) return Array.from({ length: total }, (_, index) => index + 1);
  const pages: Array<number | 'ellipsis-left' | 'ellipsis-right'> = [1];
  if (current > 3) pages.push('ellipsis-left');
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  for (let page = start; page <= end; page += 1) pages.push(page);
  if (current < total - 2) pages.push('ellipsis-right');
  pages.push(total);
  return pages;
};

const StatusSwitch = ({ record, pending, onToggle }: {
  record: SettingsRecord;
  pending: boolean;
  onToggle: (record: SettingsRecord) => void;
}) => {
  const active = record.status === 'active';
  return (
    <div className="settings-status-control">
      <button
        className="settings-status-switch"
        type="button"
        role="switch"
        aria-checked={active}
        aria-label={`${active ? 'Deactivate' : 'Activate'} ${settingsRecordLabel(record)}`}
        disabled={pending}
        onClick={() => onToggle(record)}
      >
        <span />
      </button>
      <span className={`status-badge ${active ? 'active' : 'inactive'}`}>{active ? 'Active' : 'Inactive'}</span>
    </div>
  );
};

const SettingsDetailsDrawer = ({ definition, record, onClose }: {
  definition: SettingsModuleDefinition;
  record: SettingsRecord;
  onClose: () => void;
}) => {
  const sections = [...new Set(definition.fields.map((field) => field.section))];
  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    document.body.classList.add('modal-open');
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      document.body.classList.remove('modal-open');
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [onClose]);

  return (
    <div className="settings-drawer-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <aside className="settings-drawer" role="dialog" aria-modal="true" aria-labelledby="settings-details-title">
        <header className="settings-drawer-header">
          <div><p className="eyebrow">Record details</p><h2 id="settings-details-title">{settingsRecordLabel(record)}</h2><p>Complete {definition.singular} information.</p></div>
          <button className="settings-drawer-close" type="button" onClick={onClose} aria-label="Close details">x</button>
        </header>
        <div className="settings-form-body settings-details-body">
          {sections.map((section) => (
            <section className="settings-form-section" key={section}>
              <h3>{section}</h3>
              <dl className="settings-details-grid">
                {definition.fields.filter((field) => field.section === section).map((field) => {
                  const value = getValueAtPath(record, field.path);
                  const optionLabel = typeof value === 'string'
                    ? field.options?.find((option) => option.value === value)?.label
                    : undefined;
                  const displayValue = field.type === 'textarea' && typeof value === 'string'
                    ? stripHtml(value) || '-'
                    : optionLabel ?? formatSettingsValue(value, field.type === 'date' ? 'date' : field.lookupResource ? 'reference' : 'text');
                  return (
                    <div className={field.fullWidth ? 'full' : ''} key={field.path}>
                      <dt>{field.label}</dt>
                      <dd>{field.type === 'image' && typeof value === 'string' && value
                        ? <img className="settings-details-image" src={value} alt={field.label} />
                        : displayValue}</dd>
                    </div>
                  );
                })}
              </dl>
            </section>
          ))}
        </div>
        <footer className="settings-drawer-actions"><button className="button button-primary" type="button" onClick={onClose}>Close</button></footer>
      </aside>
    </div>
  );
};

const ListSettingsPanel = ({ definition }: { definition: SettingsModuleDefinition }) => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<SettingsStatus | ''>('');
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [subCategoryFilter, setSubCategoryFilter] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<SettingsRecord | null>(null);
  const [viewingRecord, setViewingRecord] = useState<SettingsRecord | null>(null);
  const isCharges = definition.slug === 'charges';
  const chargeCategories = useSettingsLookup('charge-categories', isCharges);
  const chargeSubCategories = useSettingsLookup('charge-categories', isCharges && Boolean(categoryFilter), categoryFilter ? { parentId: categoryFilter } : undefined);
  const query = useSettingsList(definition.slug, page, limit, search, status, sortBy, sortOrder, categoryFilter, subCategoryFilter);
  const tableSort = useTableSort(sortBy, sortOrder, (next) => {
    setSortBy(next.key);
    setSortOrder(next.direction);
    setPage(1);
  });
  const sortableColumns = new Set([...(SETTINGS_SORTABLE_FIELDS[definition.slug] ?? []), 'createdAt']);
  const createMutation = useCreateSetting(definition.slug);
  const updateMutation = useUpdateSetting(definition.slug);
  const statusMutation = useToggleSettingStatus(definition.slug);
  const tableLookupResources = [...new Set(definition.columns.flatMap((column) => column.lookupResource ? [column.lookupResource] : []))];
  const tableLookupQueries = useQueries({
    queries: tableLookupResources.map((resource) => ({
      queryKey: settingsKeys.lookup(resource),
      queryFn: () => fetchSettingsLookup(resource),
      staleTime: 5 * 60_000,
    })),
  });
  const tableLookups = Object.fromEntries(
    tableLookupResources.map((resource, index) => [resource, tableLookupQueries[index]]),
  );

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const openCreate = () => {
    setEditingRecord(null);
    setEditorOpen(true);
  };

  const openEdit = (record: SettingsRecord) => {
    setEditingRecord(record);
    setEditorOpen(true);
  };

  const save = async (payload: Record<string, unknown>) => {
    if (editingRecord) {
      const confirmed = await confirmUpdate(`${settingsRecordLabel(editingRecord)} in ${definition.label}`);
      if (!confirmed) return;
      await updateMutation.mutateAsync({ recordId: editingRecord.id, payload });
    } else {
      await createMutation.mutateAsync({ ...payload, status: 'active' });
    }
    setEditorOpen(false);
    setEditingRecord(null);
  };

  const toggleStatus = async (record: SettingsRecord) => {
    const nextStatus: SettingsStatus = record.status === 'active' ? 'inactive' : 'active';
    const action = nextStatus === 'active' ? 'activate' : 'deactivate';
    const confirmed = await confirmAction({
      title: `${nextStatus === 'active' ? 'Activate' : 'Deactivate'} Record?`,
      description: `Are you sure you want to ${action} ${settingsRecordLabel(record)}?`,
      confirmLabel: `Yes, ${nextStatus === 'active' ? 'Activate' : 'Deactivate'}`,
    });
    if (!confirmed) return;
    statusMutation.mutate({ recordId: record.id, status: nextStatus });
  };

  const totalPages = Math.max(query.data?.pagination.totalPages ?? 1, 1);
  const total = query.data?.pagination.total ?? 0;
  const records = query.data?.data ?? [];
  const isSaving = createMutation.isPending || updateMutation.isPending;

  useEffect(() => {
    setCategoryFilter('');
    setSubCategoryFilter('');
  }, [definition.slug]);

  useEffect(() => {
    const reportedTotalPages = query.data?.pagination.totalPages;
    if (reportedTotalPages !== undefined && page !== clampSettingsPage(page, reportedTotalPages)) {
      setPage(clampSettingsPage(page, reportedTotalPages));
    }
  }, [page, query.data?.pagination.totalPages]);

  const formatCell = (record: SettingsRecord, column: SettingsModuleDefinition['columns'][number]) => {
    if (definition.slug === 'roles' && column.path === 'allPermissions') {
      return record.allPermissions === true ? <span className="settings-permission-badge"><IconShield />All permissions</span> : '-';
    }
    const value = getValueAtPath(record, column.path);
    const field = definition.fields.find((candidate) => candidate.path === column.path);
    if (definition.slug === 'notices' && column.path === 'notice' && typeof value === 'string') {
      const content = parseNoticeContent(value);
      return (
        <span className="settings-notice-cell">
          {content.images[0] ? (
            <img src={content.images[0].src} alt="" />
          ) : null}
          {content.text ? <span>{content.text}</span> : null}
        </span>
      );
    }
    if (field?.type === 'textarea' && typeof value === 'string') {
      return stripHtml(value) || '-';
    }
    if (definition.slug === 'document-headers' && column.path === 'logoDataUrl') {
      return typeof value === 'string' && value
        ? <img className="settings-table-logo" src={value} alt={`${settingsRecordLabel(record)} logo`} />
        : '-';
    }
    if (definition.slug === 'packages' && column.path === 'items' && Array.isArray(value)) {
      const labels = value.map((raw) => {
        if (typeof raw !== 'object' || raw === null) return '';
        const charge = (raw as Record<string, unknown>).charge;
        if (typeof charge === 'object' && charge !== null) return lookupRecordLabel(charge as SettingsRecord);
        return typeof charge === 'string' ? charge : '';
      }).filter(Boolean);
      return labels.join(', ') || '-';
    }
    if (definition.slug === 'charges' && column.path === 'chargeType') {
      return value === 'other-services' ? 'Other Services' : value === 'investigation' ? 'Investigation' : '-';
    }
    if (definition.slug === 'charges' && column.path === 'chargeSection') {
      return value === 'profile' ? 'Profile' : value === 'non_profile' ? 'Non-Profile' : '-';
    }
    if (!column.lookupResource || typeof value !== 'string') return formatSettingsValue(value, column.format);
    const lookup = tableLookups[column.lookupResource];
    const linkedRecord = lookup?.data?.find((item) => item.id === value);
    if (linkedRecord) return lookupRecordLabel(linkedRecord);
    return lookup?.isLoading ? 'Loading linked record...' : 'Linked record unavailable';
  };

  return (
    <>
      <div className="settings-list-head">
        <div><span className="settings-kicker">Master records</span><h2>{definition.label}</h2><p>{definition.description}</p></div>
        {/* Add button hidden for charge sections */}
        {definition.slug !== 'charge-sections' && definition.slug !== 'permissions' ? (
          <button className="button button-primary" type="button" onClick={openCreate}><span aria-hidden="true">+</span> Add {definition.singular}</button>
        ) : null}
      </div>

      <div className="settings-table-card">
        <div className="settings-table-toolbar">
          <form className="settings-search" role="search" onSubmit={submitSearch}>
            <span aria-hidden="true">Find</span>
            <input
              type="search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder={definition.searchPlaceholder}
              aria-label={`Search ${definition.label}`}
            />
            {searchInput || search ? (
              <button
                className="settings-search-clear"
                type="button"
                onClick={() => { setSearchInput(''); setSearch(''); setPage(1); }}
                aria-label="Clear search"
              >x</button>
            ) : null}
            <button className="settings-search-submit" type="submit">Search</button>
          </form>
          <div className="settings-table-filters">
            <label>
              <span>Status</span>
              <SearchableSelect
                options={statusFilterOptions}
                value={status}
                onChange={(value) => { setStatus(value as SettingsStatus | ''); setPage(1); }}
              />
            </label>
            {isCharges ? (
              <>
                <label>
                  <span>Category</span>
                  <SearchableSelect
                    options={[
                      { value: '', label: 'All categories' },
                      ...(chargeCategories.data ?? []).map((item) => ({ value: item.id, label: lookupRecordLabel(item) })),
                    ]}
                    value={categoryFilter}
                    onChange={(value) => { setCategoryFilter(value); setSubCategoryFilter(''); setPage(1); }}
                  />
                </label>
                <label>
                  <span>Sub Category</span>
                  <SearchableSelect
                    options={[
                      { value: '', label: 'All sub categories' },
                      ...(chargeSubCategories.data ?? []).map((item) => ({ value: item.id, label: lookupRecordLabel(item) })),
                    ]}
                    value={subCategoryFilter}
                    onChange={(value) => { setSubCategoryFilter(value); setPage(1); }}
                    disabled={!categoryFilter}
                  />
                </label>
              </>
            ) : null}
            <span className="settings-record-count">{total.toLocaleString()} {total === 1 ? 'record' : 'records'}</span>
          </div>
        </div>

        {statusMutation.isError ? (
          <div className="inline-alert" role="alert">
            {errorMessage(statusMutation.error, 'The status change was not saved. The previous status has been restored.')}
          </div>
        ) : null}
        {query.isLoading ? <SettingsLoading /> : null}
        {query.isError ? (
          <ErrorState
            title={`Unable to load ${definition.label.toLowerCase()}`}
            message={errorMessage(query.error, 'The settings records could not be loaded.')}
            action={{ label: 'Try again', onClick: () => void query.refetch() }}
          />
        ) : null}
        {query.isSuccess && records.length === 0 ? (
          <EmptyState
            title={search || status ? 'No matching records' : `No ${definition.label.toLowerCase()} configured`}
            message={search || status ? 'Try clearing the search or changing the status filter.' : `Add the first ${definition.singular} to begin.`}
            action={search || status
              ? { label: 'Clear filters', onClick: () => { setSearch(''); setSearchInput(''); setStatus(''); setPage(1); } }
              : { label: `Add ${definition.singular}`, onClick: openCreate }}
          />
        ) : null}
        {query.isSuccess && records.length > 0 ? (
          <>
            <div className="table-scroll settings-table-scroll">
              <table className="settings-table">
                <thead>
                  <tr>
                    {definition.columns.map((column) => sortableColumns.has(column.path)
                      ? <SortableTh key={column.path} control={tableSort} column={column.path}>{column.label}</SortableTh>
                      : <th key={column.path}>{column.label}</th>)}
                    <th>Active</th>
                    {definition.slug !== 'permissions' ? <th><span className="sr-only">Actions</span></th> : null}
                  </tr>
                </thead>
                <tbody>
                  {records.map((record) => (
                    <tr key={record.id}>
                      {definition.columns.map((column, index) => (
                        <td key={column.path}>
                          {index === 0
                            ? <strong className="settings-primary-cell">{formatCell(record, column)}</strong>
                            : formatCell(record, column)}
                        </td>
                      ))}
                      <td>
                        <StatusSwitch
                          record={record}
                          pending={query.isFetching || statusMutation.isPending && statusMutation.variables?.recordId === record.id}
                          onToggle={(record) => void toggleStatus(record)}
                        />
                      </td>
                      {definition.slug !== 'permissions' ? (
                        <td className="table-actions">
                          {definition.slug === 'roles' ? (
                            <button className="settings-edit-button" type="button" disabled={query.isFetching} onClick={() => openEdit(record)} aria-label={`Assign permissions to ${settingsRecordLabel(record)}`} title="Assign permissions"><IconShield /></button>
                          ) : null}
                          {definition.showView ? <button className="settings-edit-button" type="button" disabled={query.isFetching} onClick={() => setViewingRecord(record)}>View</button> : null}
                          <button className="settings-edit-button" type="button" disabled={query.isFetching} onClick={() => openEdit(record)} aria-label={`Edit ${settingsRecordLabel(record)}`} title="Edit"><IconPencil /></button>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="settings-pagination">
              <label>
                <span>Rows per page</span>
                <SearchableSelect
                  options={rowsPerPageOptions}
                  value={String(limit)}
                  disabled={query.isFetching}
                  onChange={(value) => { setLimit(Number(value)); setPage(1); }}
                />
              </label>
              <span className="settings-page-summary">Page {page} of {totalPages}</span>
              <nav aria-label={`${definition.label} pages`}>
                <button type="button" disabled={page <= 1 || query.isFetching} onClick={() => setPage((value) => Math.max(value - 1, 1))} aria-label="Previous page">&lt;</button>
                {pageNumbers(page, totalPages).map((item) => typeof item === 'number' ? (
                  <button
                    type="button"
                    key={item}
                    className={item === page ? 'active' : ''}
                    aria-current={item === page ? 'page' : undefined}
                    disabled={query.isFetching}
                    onClick={() => setPage(item)}
                  >{item}</button>
                ) : <span key={item}>...</span>)}
                <button type="button" disabled={page >= totalPages || query.isFetching} onClick={() => setPage((value) => Math.min(value + 1, totalPages))} aria-label="Next page">&gt;</button>
              </nav>
            </div>
          </>
        ) : null}
      </div>

      {editorOpen ? definition.editor === 'charge' ? (
        <ChargeSettingsDrawer record={editingRecord} isPending={isSaving} onClose={() => { setEditorOpen(false); setEditingRecord(null); }} onSubmit={save} />
      ) : definition.editor === 'package' ? (
        <PackageSettingsDrawer record={editingRecord} isPending={isSaving} onClose={() => { setEditorOpen(false); setEditingRecord(null); }} onSubmit={save} />
      ) : definition.editor === 'document-header' ? (
        <DocumentHeaderSettingsDrawer record={editingRecord} isPending={isSaving} onClose={() => { setEditorOpen(false); setEditingRecord(null); }} onSubmit={save} />
      ) : (
        <SettingsFormDrawer definition={definition} record={editingRecord} isPending={isSaving} onClose={() => { setEditorOpen(false); setEditingRecord(null); }} onSubmit={save} />
      ) : null}
      {viewingRecord ? <SettingsDetailsDrawer definition={definition} record={viewingRecord} onClose={() => setViewingRecord(null)} /> : null}
    </>
  );
};

const SettingsWorkspace = ({ slug }: { slug: SettingsModuleSlug }) => {
  const [searchParams] = useSearchParams();
  const definition = settingsModuleMap[slug];
  const moduleTabs = settingsModuleTabs.find((group) => (group.slugs as readonly SettingsModuleSlug[]).includes(slug));
  const primarySlug = slug === 'charge-categories' || slug === 'charge-sections' || slug === 'requisition-sections'
    ? 'charges'
    : slug === 'wards'
      ? 'beds'
      : slug;
  const usersTab = searchParams.get('tab') ?? 'users';
  return (
    <div className="settings-page page-stack">
      <div className="page-heading settings-page-heading">
        <div><p className="eyebrow">Administration</p><h1>Settings</h1><p>Manage hospital master data and platform behavior from one workspace.</p></div>
      </div>
      <div className="settings-workspace">
        <aside className="settings-module-rail" aria-label="Settings modules">
          <div className="settings-module-rail-title">
            <strong>Configuration</strong>
            <small>{settingsModules.length + settingsNavigationLinks.length} options</small>
          </div>
          <nav>
            {settingsModules.map((module) => (
              <NavLink
                key={module.slug}
                to={`/settings/${module.slug}`}
                className={module.slug === primarySlug ? 'active' : ''}
              >
                <span>{module.label}</span>
                {settingsModuleTabs.some((group) => group.slugs[0] === module.slug)
                  ? <b aria-hidden="true">&gt;</b>
                  : null}
              </NavLink>
            ))}
            {settingsNavigationLinks.map(({ label, icon: Icon, to }) => (
              <NavLink key={to} to={to} className={({ isActive }) => (isActive ? 'active' : undefined)}>
                <span className="settings-module-link-label"><Icon width="16" height="16" strokeWidth={1.8} />{label}</span>
              </NavLink>
            ))}
          </nav>
        </aside>
        <section className="settings-module-content" aria-label={definition.label}>
          {slug === 'users' ? (
            <nav className="settings-subtabs" aria-label="Users sections">
              {usersSettingsTabs.map((tab) => (
                <Link
                  key={tab.key}
                  to={`/settings/users${tab.key === 'users' ? '' : `?tab=${tab.key}`}`}
                  className={usersTab === tab.key ? 'active' : ''}
                >
                  {tab.label}
                </Link>
              ))}
            </nav>
          ) : null}
          {moduleTabs ? (
            <nav className="settings-subtabs" aria-label={`${settingsModuleMap[primarySlug].label} sections`}>
              {moduleTabs.tabs.map((tab) => (
                <NavLink key={tab.slug} to={`/settings/${tab.slug}`}>{tab.label}</NavLink>
              ))}
            </nav>
          ) : null}
          {slug === 'users' && usersTab === 'roles' ? (
            <ListSettingsPanel definition={settingsModuleMap.roles} />
          ) : slug === 'users' && usersTab === 'permissions' ? (
            <ListSettingsPanel definition={settingsModuleMap.permissions} />
          ) : definition.singleton
            ? <GeneralSettingsPanel />
            : definition.slug === 'tds-management'
              ? <TdsManagementPanel />
              : <ListSettingsPanel definition={definition} />}
        </section>
      </div>
    </div>
  );
};

export const SettingsPage = () => {
  const { moduleSlug } = useParams();
  if (!isSettingsModuleSlug(moduleSlug)) return <Navigate to="/settings/general-settings" replace />;
  return <SettingsWorkspace key={moduleSlug} slug={moduleSlug} />;
};
